import { useEffect, useRef, type RefObject } from "react";
import type { TouchGestureEvent } from "../components/Touch";

const DIRECTION_THRESHOLD = 5; // px, same as Touch.tsx
const DEFAULT_DAMPING = 0.55;

type OverscrollDragCallbacks = {
  onMove: (event: TouchGestureEvent) => void;
  onEnd: (event: TouchGestureEvent) => void;
};

type UseOverscrollDragOptions = {
  scrollRef: RefObject<HTMLElement | null>;
  callbacks: OverscrollDragCallbacks;
  enabled: boolean;
  damping?: number;
};

const isTouch = typeof window !== "undefined" && "ontouchstart" in window;

export const useOverscrollDrag = ({
  scrollRef,
  callbacks,
  enabled,
  damping = DEFAULT_DAMPING,
}: UseOverscrollDragOptions) => {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const dampingRef = useRef(damping);
  dampingRef.current = damping;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;

    let isTracking = false;
    let startY = 0;
    let startT = new Date();
    let isDragging = false;
    let decided = false;
    let overscrollStartY = 0;
    let velocityStartT = new Date();
    let lastRawShiftSign = 0;

    const startEvent = isTouch ? "touchstart" : "mousedown";
    const moveEvent = isTouch ? "touchmove" : "mousemove";
    const endEvents = isTouch
      ? (["touchend", "touchcancel"] as const)
      : (["mouseup", "mouseleave"] as const);

    const getY = (e: TouchEvent | MouseEvent): number => {
      if ("clientY" in e) return e.clientY;
      return e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY ?? 0;
    };

    const handleStart = (e: Event) => {
      const te = e as TouchEvent | MouseEvent;
      isTracking = true;
      startY = getY(te);
      startT = new Date();
      isDragging = false;
      decided = false;
    };

    const handleMove = (e: Event) => {
      if (!isTracking) return;

      const te = e as TouchEvent | MouseEvent;

      // Abort on multi-touch
      if (!isTouch === false && "touches" in te && te.touches.length > 1) {
        if (isDragging) {
          handleEnd(e);
        }
        return;
      }

      const currentY = getY(te);
      const deltaY = currentY - startY;

      if (isDragging) {
        e.preventDefault();
        const rawShift = currentY - overscrollStartY;

        // Reset velocity reference on direction reversal
        const sign = rawShift > 0 ? 1 : rawShift < 0 ? -1 : 0;
        if (sign !== 0 && sign !== lastRawShiftSign) {
          velocityStartT = new Date();
          lastRawShiftSign = sign;
        }

        const dampedShift = rawShift * dampingRef.current;

        callbacksRef.current.onMove({
          startX: 0,
          startY: overscrollStartY,
          startT,
          shiftY: dampedShift,
          shiftX: 0,
          shiftXAbs: 0,
          shiftYAbs: Math.abs(dampedShift),
          isPressed: true,
          isY: true,
          isX: false,
          isSlide: true,
          isSlideX: false,
          isSlideY: true,
          originalEvent: te,
        });
        return;
      }

      if (decided) return;

      if (Math.abs(deltaY) < DIRECTION_THRESHOLD) return;

      if (el.scrollTop <= 0 && deltaY > 0) {
        isDragging = true;
        decided = true;
        overscrollStartY = currentY;
        velocityStartT = new Date();
        lastRawShiftSign = 1;
        e.preventDefault();
      } else {
        decided = true;
      }
    };

    const handleEnd = (e: Event) => {
      if (!isTracking) return;

      if (!isDragging) {
        isDragging = false;
        decided = false;
        isTracking = false;
        return;
      }

      const te = e as TouchEvent | MouseEvent;
      const currentY = getY(te);
      const rawShift = currentY - overscrollStartY;

      // Use raw (undamped) shift for onEnd so velocity calculation and
      // mode-snap zones work correctly. The visual jump from damped to raw
      // is invisible because setShift(0) + CSS transition fire immediately.
      // Use velocityStartT (reset on direction reversal) so fling detection
      // reflects the most recent directional movement, not the entire gesture.
      callbacksRef.current.onEnd({
        startX: 0,
        startY: overscrollStartY,
        startT: velocityStartT,
        shiftY: rawShift,
        shiftX: 0,
        shiftXAbs: 0,
        shiftYAbs: rawShift,
        isPressed: false,
        isY: true,
        isX: false,
        isSlide: true,
        isSlideX: false,
        isSlideY: true,
        originalEvent: te,
      });

      isDragging = false;
      decided = false;
      isTracking = false;
    };

    el.addEventListener(startEvent, handleStart, { passive: true });
    window.addEventListener(moveEvent, handleMove, {
      capture: true,
      passive: false,
    });
    for (const evt of endEvents) {
      window.addEventListener(evt, handleEnd, { capture: true });
    }

    return () => {
      el.removeEventListener(startEvent, handleStart);
      window.removeEventListener(moveEvent, handleMove, { capture: true });
      for (const evt of endEvents) {
        window.removeEventListener(evt, handleEnd, { capture: true });
      }
    };
  }, [scrollRef, enabled]);
};
